import axios from 'axios'
import { MIN_CHUNK_SIZE } from '@/utils/constants'
import { cutDomain } from '@/utils/helpers'
import store from '@/store'


export default class Load extends File {

  constructor(files, successHandler, chunkLoadHandler, errorHandler) {
    super(files, files[0].name)
    this.successHandler = successHandler
    this.chunkLoadHandler = chunkLoadHandler
    this.errorHandler = errorHandler
    this.aborted = undefined
    this.abortController = undefined
    this.failedStep = undefined
    this.chunkSize = undefined
    this.chunks = []
    this.bucket = {}
    this.fileType = this.name.split('.').reverse()[0]
    this.requestConfig = {
      baseURL: process.env.VUE_APP_UPLOAD_URL.replace(/\/upload$/, '')
    }
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/octet-stream'
      }
    })
    this.loaded = 0
    this.uploadIsSimple = this.size <= MIN_CHUNK_SIZE
  }

  apply() {
    this.aborted = false
    this.abortController = new AbortController()
    if (this.uploadIsSimple) {
      this.simpleUpload()
    } else {
      this.chunk()
    }
  }

  retry() {
    this.abortController = new AbortController()
    this[this.failedStep]()
  }

  async simpleUpload() {
    const data = {file: this}
    try {
      const response = await window.axios.postForm('upload', data, {
        ...this.requestConfig,
        signal: this.abortController.signal
      })
      const path = response.data.response.file
      this.chunkLoadHandler(100)
      this.successHandler(path)
    } catch (e) {
      console.log('Ошибка в методе complete:', e)
      this.emitError('simpleUpload')
    }
  }

  chunk() {
    let byteStart = 0
    const MAX_CHUNK_COUNT = 100

    this.chunkSize = Math.max(Math.ceil(this.size / MAX_CHUNK_COUNT), MIN_CHUNK_SIZE)

    while (byteStart < this.size) {
      this.chunks.push({ file: this.slice(byteStart, byteStart + this.chunkSize) })
      byteStart += this.chunkSize
    }

    this.bucketInit()
  }

  async bucketInit() {
    const requestData = {
      type: this.fileType,
      countFiles: this.chunks.length
    }
    try {
      const { data: { response } } = await window.axios.post(`multipart`, requestData, {
        ...this.requestConfig,
        signal: this.abortController.signal
      })
      response.urls.forEach((url, idx) => {
        this.chunks[idx].uploadUrl = url
      })
      this.bucket.uploadId = response.uploadId
      this.bucket.fileName = response.fileName
      this.load()
    } catch (e) {
      console.log('Ошибка в методе bucketInit:', e)
      this.emitError('bucketInit')
    }
  }

  async load() {
    const loadChunk = async chunk => {
      const MAX_RETRIES = 1
      let retriesCount = 0
      const tryLoad = async () => {
        try {
          const response = await this.axiosInstance.put(chunk.uploadUrl, chunk.file, {
            signal: this.abortController.signal
          })
          this.emitChunkLoad()
          return (response.headers.etag).replace(/"/g, '')
        } catch (e) {
          console.log('Ошибка в методе tryLoad:', e)
        }
      }

      while (!chunk.etag && (retriesCount <= MAX_RETRIES)) {
        // eslint-disable-next-line no-await-in-loop,require-atomic-updates
        chunk.etag = await tryLoad()
        retriesCount++
      }
    }

    for (const chunk of this.chunks.filter(item => !item.etag)) {
      if (this.aborted) break
      // eslint-disable-next-line no-await-in-loop
      await loadChunk(chunk)
    }

    if (this.chunks.some(item => !item.etag)) {
      this.emitError('load')
      return
    }

    this.complete()
  }

  async complete() {
    const body = {
      parts: []
    }
    this.chunks.forEach((chunk, idx) => {
      body.parts.push({
        PartNumber: idx + 1,
        ETag: chunk.etag
      })
    })
    body.uploadId = this.bucket.uploadId
    body.fileName = this.bucket.fileName

    try {
      const data = { completeMultipartUpload: JSON.stringify(body) }
      const { data: { response: { file } } } = await window.axios.post(`complete`, data, {
        ...this.requestConfig,
        signal: this.abortController.signal
      })
      this.successHandler(cutDomain(file))
    } catch (e) {
      console.log('Ошибка в методе complete:', e)
      this.emitError('complete')
    }
  }

  emitChunkLoad() {
    let percent
    this.loaded += this.chunkSize
    percent = Math.ceil(this.loaded * 100 / this.size)
    this.chunkLoadHandler(Math.min(percent, 100))
  }

  emitError(name) {
    if (this.aborted) return
    this.failedStep = name
    this.errorHandler()
    store.$app.$notifyError('При загрузке файла произошла ошибка. Для повторной попытки нажмите "Повторить".')
  }

  async abort() {
    this.aborted = true
    this.abortController.abort()
    if (_.isEmpty(this.bucket)) return
    const data = { abortMultipartUpload: JSON.stringify(this.bucket) }
    try {
      await window.axios.post(`abort`, data, this.requestConfig)
    } catch (e) {
      console.log(e)
    }
  }
}
